import React from "react";
import { styled } from "@linaria/react";
import formatDate from "@ui/i18n/formatDate";
import { color, easingBounce, easingBounceMs } from "@ui";
import classNames from "classnames";
import getActivityDescription from "./getActivityDescription";
import Initial from "@ui/Initial";
import Link from "@ui/Link";
import LoadingBoundary from "@ui/LoadingBoundary";
import type { Project } from "../../api/useProjects";
import t from "../../i18n";
import { fonts } from "@ui/Theme";
import type { Activity, ActivityIndexJSON, ActivityType } from "../../api/useActivities";
import Badge from "@ui/Badge";
import { InformationLine, MetroscopeLogo } from "@ui/IconV2/react";
import { useTranslations } from "../../api/useTranslations";
import { toPath } from "../../router/helpers/path";

const EXPANDABLE = "expandable";
const OPENED = "opened";

const ActivityWrapper = styled.div`
  position: relative;
  width: 100%;
  padding: 12px 25px;
  font: ${fonts.body1};

  &.${EXPANDABLE} {
    cursor: pointer;
  }
  &:hover {
    background-color: ${color.titanium50};
  }
  &:not(:last-child) {
    border-bottom: 1px solid ${color.titanium100};
  }
  &.largeSideMargin {
    padding: 15px 45px;
  }
`;

const DescriptionHeader = styled.div`
  display: flex;
  align-items: center;
  font: ${fonts.body2};
  gap: 16px;
`;

const DescriptionWrapper = styled.div`
  position: relative;
  width: 100%;
  font: ${fonts.body1};
`;

const Title = styled.div`
  position: relative;
  display: flex;
  column-gap: 3px;
  margin-top: 10px;
`;

const LogoTitle = styled.div``;
const DescriptionTitle = styled.div`
  overflow-x: auto;
  flex: 1;
`;

const StyledDate = styled.div`
  color: ${color.carbon700};
`;

const Details = styled.div``;

const ContainerProjectName = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: ${color.carbon700};
`;

const DetailsTitle = styled.p`
  font: ${fonts.body2};
  color: ${color.carbon700};
  display: flex;
  align-items: center;
  margin-top: 10px;
  :after {
    cursor: pointer;
    display: block;
    position: relative;
    top: -1px;
    content: "";
    width: 0.5em;
    height: 0.5em;
    border-width: 0 1px 1px 0;
    border-style: solid;
    transition: transform ${easingBounceMs}ms ${easingBounce};
    transform-origin: 60% 60%;
    margin-left: 8px;
  }
  &:not(.${OPENED}):after {
    transform: rotate(45deg);
  }
  &.${OPENED}:after {
    transform: rotate(225deg);
  }
`;

const DetailsInfo = styled.div`
  font: ${fonts.body2};
  margin-top: 0;
  visibility: hidden;
  overflow: hidden;
  max-height: 0;

  &.${OPENED} {
    margin-top: 10px;
    max-height: 300px;
    visibility: visible;
    transition: all 0.1s linear;
    overflow: auto;

    /* hide scrollbar while animating */
    @keyframes hide-scroll {
      from,
      to {
        overflow: hidden;
      }
    }
    animation: 0.2s backwards hide-scroll;
  }
`;

const StyledLink = styled(Link)`
  color: ${color.carbon600};

  &:hover {
    color: ${color.lightNitrogen600};
    text-decoration: underline;
  }
`;

const LogoWrapper = styled.div``;

const StyledLogo = styled(MetroscopeLogo)`
  height: 18px;
  width: 18px;
  margin-right: 5px;
`;

const BadgeProjectDeleted = ({ projectName }: { projectName: string }) => (
  <ContainerProjectName>
    <p>{projectName}</p>
    <Badge
      size="s"
      icon={<InformationLine color={color.darkNitrogen600} />}
      color={color.carbon900}
      backgroundColor={color.lightNitrogen50}
    >
      {t("activity.badge.project.deleted")}
    </Badge>
  </ContainerProjectName>
);

interface P {
  data: Activity;
  timeZone: string | null;
  embeded: boolean;
  projectsIndex: { [id: string]: Project };
}

const ActivityItem = ({ data, timeZone, embeded, projectsIndex }: P) => {
  const [expanded, setExpanded] = React.useState<boolean>(false);
  const translations = useTranslations();
  const description = getActivityDescription(
    data as { type: ActivityType } & ActivityIndexJSON[typeof data.type],
    translations,
    data.projectId ? (projectsIndex[data.projectId]?.timezone ?? null) : null,
  );
  const project = description.projectId ? projectsIndex[description.projectId] : undefined;

  const { updatedAt, userName, userId, projectName } = data; //authorLogin
  const formatedDate = formatDate(new Date(updatedAt), "time", timeZone);

  const hasInitial = Boolean(userName);

  const handleActivityClick = (e: React.MouseEvent) => {
    const target = e.target as HTMLElement;
    if (target.localName === "a") return;
    setExpanded(!expanded);
  };

  return (
    <ActivityWrapper
      onClick={handleActivityClick}
      className={classNames({
        [EXPANDABLE]: description.details,
        largeSideMargin: !embeded,
      })}
    >
      <DescriptionHeader>
        <StyledDate>{formatedDate}</StyledDate>

        {embeded ? null : project ? (
          <StyledLink
            to={toPath("/projects/:projectId", {
              projectId: project.id,
            })}
          >
            {project.name}
          </StyledLink>
        ) : projectName ? (
          <BadgeProjectDeleted projectName={projectName} />
        ) : null}
      </DescriptionHeader>
      <DescriptionWrapper>
        <Title>
          <LogoTitle>
            {hasInitial ? (
              <Initial login={userId} name={userName ?? userId} />
            ) : (
              <LogoWrapper>
                <StyledLogo />
              </LogoWrapper>
            )}
          </LogoTitle>
          <DescriptionTitle>{description.title}</DescriptionTitle>
        </Title>
        {description.details && (
          <Details>
            <DetailsTitle className={classNames({ [OPENED]: expanded })}>
              {t("activity.body.details.title")}
            </DetailsTitle>
            <DetailsInfo className={classNames({ [OPENED]: expanded })}>
              <LoadingBoundary>{expanded ? description.details() : null}</LoadingBoundary>
            </DetailsInfo>
          </Details>
        )}
      </DescriptionWrapper>
    </ActivityWrapper>
  );
};

export default ActivityItem;
